import React from 'react';
import { Button } from '../../../components/ui/button';
import { RefreshCw, MessageSquare, Send, CheckCircle, AlertCircle, Type, CreditCard, DollarSign, Image as ImageIcon } from 'lucide-react';
import ImagePickerModal from './ImagePickerModal';

const SettingsAdmin = () => {
  const [settings, setSettings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  // SMS Test State
  const [smsStatus, setSmsStatus] = React.useState(null);
  const [testPhone, setTestPhone] = React.useState('');
  const [testMessage, setTestMessage] = React.useState('Test message from 1st Ave ICP! 🍦');
  const [sendingTest, setSendingTest] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  
  // Section Titles State
  const [sectionTitles, setSectionTitles] = React.useState({});
  const [savingTitles, setSavingTitles] = React.useState(false);
  const [titlesUpdated, setTitlesUpdated] = React.useState(false);
  
  // SMS Credits State (Super Admin)
  const [smsCredits, setSmsCredits] = React.useState(null);
  const [rechargeAmount, setRechargeAmount] = React.useState('');
  const [rechargeNote, setRechargeNote] = React.useState('');
  const [rechargingCredits, setRechargingCredits] = React.useState(false);
  const [rechargeResult, setRechargeResult] = React.useState(null);
  const [userRole, setUserRole] = React.useState(null);
  const [showFooterPicker, setShowFooterPicker] = React.useState(false);
  const [correctBalance, setCorrectBalance] = React.useState('');
  const [correctCount, setCorrectCount] = React.useState('');
  const [correctNote, setCorrectNote] = React.useState('');
  const [correctingBalance, setCorrectingBalance] = React.useState(false);
  const [correctResult, setCorrectResult] = React.useState(null);
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [changingPassword, setChangingPassword] = React.useState(false);
  const [passwordResult, setPasswordResult] = React.useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched settings from API:', data);
        setSettings({
          business_name: data.business_name || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          // Individual day hours
          hours_mon: data.hours_mon || 'Closed',
          hours_tue: data.hours_tue || '',
          hours_wed: data.hours_wed || '',
          hours_thu: data.hours_thu || '',
          hours_fri: data.hours_fri || '',
          hours_sat: data.hours_sat || '',
          hours_sun: data.hours_sun || '',
          // Legacy fields (keep for backwards compatibility)
          hours_weekday: data.hours_weekday || '',
          hours_weekend: data.hours_weekend || '',
          facebook_url: data.facebook_url || '',
          instagram_url: data.instagram_url || '',
          twitter_url: data.twitter_url || '',
          tiktok_url: data.tiktok_url || '',
          youtube_url: data.youtube_url || '',
          // Contest notification settings
          contest_notifications_enabled: data.contest_notifications_enabled !== false,
          contest_notification_email: data.contest_notification_email || 'emtexas@gmail.com',
          // Footer decoration image
          footer_image: data.footer_image || '/images/misc/footer-icecream.png'
        });
      } else {
        setError(`Failed to load settings (Status: ${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drawings/blast/sms/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsStatus(data);
      }
    } catch (err) {
      console.error('Error fetching SMS status:', err);
    }
  };

  const sendTestSms = async () => {
    if (!testPhone.trim()) {
      alert('Please enter a phone number');
      return;
    }
    if (!testMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    setSendingTest(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drawings/sms/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_phone: testPhone,
          message: testMessage
        })
      });

      const result = await response.json();
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setSendingTest(false);
    }
  };

  const fetchSectionTitles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/menu-sections/titles`);
      if (response.ok) {
        const data = await response.json();
        setSectionTitles(data);
      }
    } catch (err) {
      console.error('Error fetching section titles:', err);
    }
  };

  const saveSectionTitles = async () => {
    setSavingTitles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/menu-sections/titles`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(sectionTitles)
      });
      if (response.ok) {
        setTitlesUpdated(true);
        setTimeout(() => setTitlesUpdated(false), 3000);
      } else {
        alert('Error saving section titles');
      }
    } catch (err) {
      console.error('Error saving section titles:', err);
      alert('Error saving section titles');
    } finally {
      setSavingTitles(false);
    }
  };

  // SMS Credits functions (Super Admin only)
  const fetchSmsCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sms-credits/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsCredits(data);
      }
    } catch (err) {
      console.error('Error fetching SMS credits:', err);
    }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordResult({ success: false, message: 'Please fill in all fields' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordResult({ success: false, message: 'New passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordResult({ success: false, message: 'New password must be at least 8 characters' });
      return;
    }

    setChangingPassword(true);
    setPasswordResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setPasswordResult({ success: true, message: 'Password changed successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordResult({ success: false, message: data.detail || 'Failed to change password' });
      }
    } catch (err) {
      setPasswordResult({ success: false, message: err.message });
    } finally {
      setChangingPassword(false);
    }
  };

  const rechargeCredits = async () => {
    const amount = parseFloat(rechargeAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setRechargingCredits(true);
    setRechargeResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/sms-credits/recharge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount, note: rechargeNote })
      });

      const data = await response.json();
      
      if (response.ok) {
        setRechargeResult({ success: true, message: data.message, new_balance: data.new_balance });
        setRechargeAmount('');
        setRechargeNote('');
        fetchSmsCredits(); // Refresh balance
      } else {
        setRechargeResult({ success: false, message: data.detail || 'Failed to recharge' });
      }
    } catch (err) {
      setRechargeResult({ success: false, message: err.message });
    } finally {
      setRechargingCredits(false);
    }
  };

  const correctBalanceSubmit = async () => {
    const bal = parseFloat(correctBalance);
    if (isNaN(bal) || bal < 0) {
      alert('Please enter a valid balance amount');
      return;
    }

    setCorrectingBalance(true);
    setCorrectResult(null);

    try {
      const token = localStorage.getItem('token');
      const body = { new_balance: bal, note: correctNote || 'Manual balance correction' };
      if (correctCount) body.new_sms_count = parseInt(correctCount);

      const response = await fetch(`${API_URL}/api/sms-credits/correct-balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (response.ok) {
        setCorrectResult({ success: true, message: data.message });
        setCorrectBalance('');
        setCorrectCount('');
        setCorrectNote('');
        fetchSmsCredits();
      } else {
        setCorrectResult({ success: false, message: data.detail || 'Failed to correct balance' });
      }
    } catch (err) {
      setCorrectResult({ success: false, message: err.message });
    } finally {
      setCorrectingBalance(false);
    }
  };


  React.useEffect(() => {
    fetchSettings();
    fetchSmsStatus();
    fetchSectionTitles();
    fetchSmsCredits();
    
    // Get user role from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        alert('Error saving settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-pink-500 mb-2" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error || 'Failed to load settings'}</p>
          <Button onClick={fetchSettings} className="bg-pink-500 hover:bg-pink-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Site Settings</h2>
          <p className="text-gray-600 text-sm mt-1">Configure your business information</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button 
            onClick={fetchSettings} 
            variant="outline" 
            className="gap-2 flex-1 sm:flex-none"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className="bg-pink-500 hover:bg-pink-600 text-white gap-2 flex-1 sm:flex-none"
          >
            {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        {/* Business Info */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">📍 Business Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-2">Business Name</label>
              <input
                type="text"
                value={settings.business_name}
                onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter business name"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Phone Number</label>
              <input
                type="tel"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter phone number"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold mb-2">Address</label>
              <input
                type="text"
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter business address"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold mb-2">Email</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter email address"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold mb-2">Footer Decoration Image</label>
              <div className="flex items-center gap-3" data-testid="settings-footer-image">
                <div className="w-20 h-20 rounded-lg border-2 border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
                  {settings.footer_image ? (
                    <img src={settings.footer_image} alt="Footer" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-6 h-6" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500 truncate mb-2">{settings.footer_image || 'No image selected'}</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowFooterPicker(true)} className="gap-1">
                    <ImageIcon className="w-4 h-4" /> Change Footer Image
                  </Button>
                  {settings.footer_image !== '/images/misc/footer-icecream.png' && (
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, footer_image: '/images/misc/footer-icecream.png' })}
                      className="ml-3 text-xs text-gray-500 hover:text-pink-600 underline"
                      data-testid="settings-footer-image-reset"
                    >
                      Reset to default
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Hours */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">🕐 Business Hours</h3>
          <p className="text-sm text-gray-600 mb-4">Set hours for each day. Type "Closed" for days you're not open.</p>
          <div className="grid gap-3">
            {[
              { key: 'hours_mon', label: 'Monday' },
              { key: 'hours_tue', label: 'Tuesday' },
              { key: 'hours_wed', label: 'Wednesday' },
              { key: 'hours_thu', label: 'Thursday' },
              { key: 'hours_fri', label: 'Friday' },
              { key: 'hours_sat', label: 'Saturday' },
              { key: 'hours_sun', label: 'Sunday' }
            ].map(day => (
              <div key={day.key} className="flex items-center gap-4">
                <label className="w-28 text-sm font-bold">{day.label}</label>
                <input
                  type="text"
                  value={settings[day.key] || ''}
                  onChange={(e) => setSettings({ ...settings, [day.key]: e.target.value })}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                    settings[day.key]?.toLowerCase() === 'closed' ? 'bg-gray-100 text-gray-500' : ''
                  }`}
                  placeholder="e.g., 12:00 PM - 9:00 PM or Closed"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Social Media */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">📱 Social Media Links</h3>
          <p className="text-sm text-gray-600 mb-4">Enter the full URL for each social media profile. Leave blank if not applicable.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-2">Facebook URL</label>
              <input
                type="url"
                value={settings.facebook_url}
                onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                  settings.facebook_url ? 'bg-green-50 border-green-300' : ''
                }`}
                placeholder="https://facebook.com/yourpage"
              />
              {settings.facebook_url && (
                <p className="text-xs text-green-600 mt-1">✓ Facebook URL is set</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Instagram URL</label>
              <input
                type="url"
                value={settings.instagram_url}
                onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                  settings.instagram_url ? 'bg-green-50 border-green-300' : ''
                }`}
                placeholder="https://instagram.com/yourhandle"
              />
              {settings.instagram_url && (
                <p className="text-xs text-green-600 mt-1">✓ Instagram URL is set</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">TikTok URL</label>
              <input
                type="url"
                value={settings.tiktok_url}
                onChange={(e) => setSettings({ ...settings, tiktok_url: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                  settings.tiktok_url ? 'bg-green-50 border-green-300' : ''
                }`}
                placeholder="https://tiktok.com/@yourhandle"
              />
              {settings.tiktok_url && (
                <p className="text-xs text-green-600 mt-1">✓ TikTok URL is set</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Twitter/X URL</label>
              <input
                type="url"
                value={settings.twitter_url}
                onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                  settings.twitter_url ? 'bg-green-50 border-green-300' : ''
                }`}
                placeholder="https://twitter.com/yourhandle"
              />
              {settings.twitter_url && (
                <p className="text-xs text-green-600 mt-1">✓ Twitter URL is set</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold mb-2">YouTube Channel URL</label>
              <input
                type="url"
                value={settings.youtube_url}
                onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                className={`w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none ${
                  settings.youtube_url ? 'bg-green-50 border-green-300' : ''
                }`}
                placeholder="https://youtube.com/@yourchannel"
              />
              {settings.youtube_url && (
                <p className="text-xs text-green-600 mt-1">✓ YouTube URL is set</p>
              )}
            </div>
          </div>
        </div>

        {/* Email Notifications */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">📧 Email Notifications</h3>
          <p className="text-sm text-gray-600 mb-4">Configure email notifications for contest entries</p>
          
          <div className="space-y-4">
            {/* Toggle for contest notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
              <div>
                <label className="font-bold">Contest Entry Notifications</label>
                <p className="text-sm text-gray-600">Receive email when someone enters the drawing</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.contest_notifications_enabled}
                  onChange={(e) => setSettings({ ...settings, contest_notifications_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
              </label>
            </div>
            
            {/* Notification email address */}
            <div className={settings.contest_notifications_enabled ? '' : 'opacity-50'}>
              <label className="block text-sm font-bold mb-2">Contest Notification Email</label>
              <input
                type="email"
                value={settings.contest_notification_email}
                onChange={(e) => setSettings({ ...settings, contest_notification_email: e.target.value })}
                disabled={!settings.contest_notifications_enabled}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none disabled:bg-gray-100"
                placeholder="emtexas@gmail.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Contest entry notifications will be sent to this email address
              </p>
            </div>
            
            {/* Info box about contact form */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Contact form submissions are sent to <strong>1staveicp@gmail.com</strong> with a copy to larry@web-techservices.com. This is configured separately from contest notifications.
              </p>
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="mt-8 bg-gray-50 rounded-xl border-2 border-gray-200 p-6">
          <h3 className="text-lg font-bold mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
            🔐 Change Password
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-bold mb-2">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-2">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                placeholder="Confirm new password"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={changePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="bg-gray-700 hover:bg-gray-800 gap-2"
            >
              {changingPassword ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
            
            {passwordResult && (
              <div className={`flex items-center gap-2 ${passwordResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {passwordResult.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{passwordResult.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* Menu Section Titles */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">📝 Menu Section Titles</h3>
          <p className="text-sm text-gray-600 mb-4">Customize the section titles displayed on the Menu page</p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { key: 'ice_cream_nachos', label: 'Ice Cream Nachos Section' },
              { key: 'ice_cream_stackers', label: 'Ice Cream Stackers Section' },
              { key: 'scoops', label: 'Scoops Section' },
              { key: 'milkshakes', label: 'Milkshakes Section' },
              { key: 'ice_cream_treats', label: 'Ice Cream Treats Section' },
              { key: 'energy_drinks', label: 'Energy Drinks Section' },
              { key: 'coffees', label: 'Coffees Section' },
              { key: 'pastries', label: 'Pastries Section' },
              { key: 'all_specialties', label: 'All Specialties Section' },
              { key: 'seasonal_flavors', label: 'Seasonal Flavors Section' }
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-bold mb-1">{label}</label>
                <input
                  type="text"
                  value={sectionTitles[key] || ''}
                  onChange={(e) => setSectionTitles({ ...sectionTitles, [key]: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder={label.replace(' Section', '')}
                />
              </div>
            ))}
          </div>
          
          <div className="mt-4 flex items-center gap-3">
            <Button 
              onClick={saveSectionTitles} 
              disabled={savingTitles}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {savingTitles ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Type className="w-4 h-4" />
                  Save Section Titles
                </>
              )}
            </Button>
            {titlesUpdated && (
              <span className="text-green-600 font-medium flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Titles saved!
              </span>
            )}
          </div>
        </div>

        {/* SMS Testing Section */}
        <div>
          <h3 className="text-lg font-bold mb-4 pb-2 border-b">💬 SMS Testing</h3>
          
          {/* SMS Status */}
          {smsStatus && (
            <div className={`mb-6 p-4 rounded-xl border-2 ${
              smsStatus.status === 'ready' 
                ? 'bg-green-50 border-green-300' 
                : 'bg-yellow-50 border-yellow-300'
            }`}>
              <div className="flex items-center gap-3">
                {smsStatus.status === 'ready' ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                )}
                <div>
                  {smsStatus.status === 'ready' ? (
                    <>
                      <h4 className="font-bold text-green-800">✅ SMS Ready</h4>
                      <p className="text-sm text-green-700">
                        Telnyx is configured. Sending from: <strong>{smsStatus.phone_number}</strong>
                      </p>
                    </>
                  ) : (
                    <>
                      <h4 className="font-bold text-yellow-800">⚠️ SMS Not Configured</h4>
                      <p className="text-sm text-yellow-700">
                        Add TELNYX_API_KEY and TELNYX_PHONE_NUMBER to enable SMS.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Test SMS Form */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h4 className="font-bold mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Send Test SMS
            </h4>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-bold mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-green-500 focus:outline-none"
                  placeholder="817-320-5334"
                />
                <p className="text-xs text-gray-500 mt-1">Enter your phone number to receive a test message</p>
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Test Message</label>
                <input
                  type="text"
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-green-500 focus:outline-none"
                  placeholder="Test message from 1st Ave ICP!"
                />
                <p className="text-xs text-gray-500 mt-1">{testMessage.length}/160 characters</p>
              </div>
            </div>

            <Button
              onClick={sendTestSms}
              disabled={sendingTest || !smsStatus?.configured}
              className="bg-green-600 hover:bg-green-700 text-white gap-2"
            >
              {sendingTest ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Test SMS
                </>
              )}
            </Button>

            {/* Test Result */}
            {testResult && (
              <div className={`mt-4 p-4 rounded-lg ${
                testResult.success ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
              }`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                    {testResult.message}
                  </span>
                </div>
                {testResult.success && testResult.to && (
                  <p className="text-sm text-green-700 mt-1">
                    Sent to: {testResult.to} from {testResult.from}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SMS Credit Management - Super Admin Only */}
        {userRole === 'super_admin' && (
          <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200 p-6">
            <h3 className="text-lg font-bold mb-4 pb-2 border-b border-purple-200 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-600" />
              SMS Credit Management (Super Admin)
            </h3>
            
            {smsCredits && (
              <div className="space-y-6">
                {/* Current Balance */}
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 border-2 border-purple-200">
                    <p className="text-sm text-gray-600">Current Balance</p>
                    <p className={`text-3xl font-black ${smsCredits.is_low ? 'text-red-600' : 'text-green-600'}`}>
                      ${smsCredits.balance.toFixed(2)}
                    </p>
                    {smsCredits.is_low && (
                      <p className="text-xs text-red-500 mt-1">⚠️ Low balance</p>
                    )}
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-blue-200">
                    <p className="text-sm text-gray-600">SMS Remaining</p>
                    <p className="text-3xl font-black text-blue-600">{smsCredits.sms_remaining}</p>
                    <p className="text-xs text-gray-500 mt-1">@ ${smsCredits.cost_per_sms}/msg</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-green-200">
                    <p className="text-sm text-gray-600">Total Recharged</p>
                    <p className="text-3xl font-black text-green-600">${smsCredits.total_recharged.toFixed(2)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border-2 border-orange-200">
                    <p className="text-sm text-gray-600">Total SMS Sent</p>
                    <p className="text-3xl font-black text-orange-600">{smsCredits.sms_sent_count}</p>
                    <p className="text-xs text-gray-500 mt-1">${smsCredits.total_spent.toFixed(2)} spent</p>
                  </div>
                </div>

                {/* Pending Refill Request */}
                {smsCredits.pending_refill_request && (
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
                    <h4 className="font-bold text-yellow-800 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Pending Refill Request
                    </h4>
                    <p className="text-yellow-700 mt-2">
                      Amount requested: <strong>${smsCredits.pending_refill_request.amount}</strong>
                      <br />
                      Requested at: {new Date(smsCredits.pending_refill_request.requested_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Recharge Form */}
                <div className="bg-white rounded-xl p-6 border-2 border-purple-200">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Add SMS Credits
                  </h4>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-bold mb-2">Amount ($)</label>
                      <input
                        type="number"
                        value={rechargeAmount}
                        onChange={(e) => setRechargeAmount(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                        placeholder="25.00"
                        min="1"
                        step="0.01"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-bold mb-2">Note (optional)</label>
                      <input
                        type="text"
                        value={rechargeNote}
                        onChange={(e) => setRechargeNote(e.target.value)}
                        className="w-full px-4 py-2 rounded-lg border-2 focus:border-purple-500 focus:outline-none"
                        placeholder="Payment received via invoice #123"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={rechargeCredits}
                      disabled={rechargingCredits || !rechargeAmount}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 gap-2"
                    >
                      {rechargingCredits ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Add Credits
                        </>
                      )}
                    </Button>
                    
                    {rechargeResult && (
                      <div className={`flex items-center gap-2 ${rechargeResult.success ? 'text-green-600' : 'text-red-600'}`}>
                        {rechargeResult.success ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <span>{rechargeResult.message}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> When the admin requests a refill, you'll receive an email notification. 
                    After receiving payment, add the credits here. The system automatically deducts $0.04 per SMS sent 
                    and shows a warning when balance falls below $10.
                  </p>
                </div>

                {/* Correct Balance Form */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Correct Balance</h4>
                  <p className="text-xs text-gray-500 mb-3">Use this to sync the in-app tracker with your actual Telnyx account balance.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm text-gray-600">Actual Balance ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={correctBalance}
                        onChange={(e) => setCorrectBalance(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="6.60"
                        data-testid="correct-balance-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Total SMS Sent</label>
                      <input
                        type="number"
                        min="0"
                        value={correctCount}
                        onChange={(e) => setCorrectCount(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="1085"
                        data-testid="correct-count-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Note</label>
                      <input
                        type="text"
                        value={correctNote}
                        onChange={(e) => setCorrectNote(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="Synced with Telnyx"
                        data-testid="correct-note-input"
                      />
                    </div>
                  </div>
                  <button
                    onClick={correctBalanceSubmit}
                    disabled={correctingBalance || !correctBalance}
                    className="mt-3 px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50"
                    data-testid="correct-balance-btn"
                  >
                    {correctingBalance ? 'Updating...' : 'Update Balance'}
                  </button>
                  {correctResult && (
                    <p className={`mt-2 text-sm ${correctResult.success ? 'text-green-600' : 'text-red-600'}`}>
                      {correctResult.message}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ImagePickerModal
        open={showFooterPicker}
        onClose={() => setShowFooterPicker(false)}
        onSelect={(url) => {
          setSettings((prev) => ({ ...prev, footer_image: url }));
          setShowFooterPicker(false);
        }}
        currentImage={settings?.footer_image || ''}
      />
    </div>
  );
};

export default SettingsAdmin;
